import { useEffect, useState, type ReactNode } from 'react';
import { initials, KIND_LABEL, STATUS_LABEL, type DonorKind, type DonorStatus, type HealthFlag } from '../../lib/fosApi';

const PATHS: Record<string, string> = {
  mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  phone: 'M6 3h4l2 5-2.5 1.5a11 11 0 0 0 5 5L16 12l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 4 5a2 2 0 0 1 2-2z',
  pin: 'M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
  users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 6.5M18 14a6 6 0 0 1 3.5 6',
  link: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  alert: 'M12 3l10 18H2zM12 10v5M12 18v.5',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  plus: 'M12 5v14M5 12h14',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM21 21l-4.5-4.5',
  heart: 'M12 20s-8-4.7-8-10.5A4.5 4.5 0 0 1 12 7a4.5 4.5 0 0 1 8 2.5C20 15.3 12 20 12 20z',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  target: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 16.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9zM12 12h.01',
  file: 'M6 3h8l4 4v14H6zM14 3v4h4M9 12h6M9 16h6',
  dollar: 'M12 3v18M16.5 7.5C15.5 6.4 14 6 12 6c-2.5 0-4 1.2-4 3s1.5 2.5 4 3 4 1.2 4 3-1.5 3-4 3c-2 0-3.5-.5-4.5-1.5',
  note: 'M5 4h14v12l-4 4H5zM15 20v-4h4M8 9h8M8 13h5',
  edit: 'M4 20l4-1 11-11-3-3L5 16zM14 6l3 3',
  swap: 'M7 7h13l-3-3M17 17H4l3 3',
  lock: 'M6 11h12v9H6zM8 11V8a4 4 0 0 1 8 0v3',
};
export function Icon({ name, size = 16 }: { name: keyof typeof PATHS | string; size?: number }) {
  return (
    <svg className="fos-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={PATHS[name] ?? PATHS.user} />
    </svg>
  );
}

export function Avatar({ name, size = 36, tone }: { name: string; size?: number; tone?: 'muted' }) {
  return <span className={`fos-avatar${tone ? ` ${tone}` : ''}`} style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }} aria-hidden>{initials(name)}</span>;
}

export function StatusChip({ status }: { status: DonorStatus }) {
  return <span className={`fos-chip status-${status}`}>{STATUS_LABEL[status]}</span>;
}
export function KindChip({ kind }: { kind: DonorKind }) {
  return <span className="fos-chip kind">{KIND_LABEL[kind]}</span>;
}

/** Flags that state a fact rather than a fault (see the server's FOS_INFORMATIONAL_HEALTH_CODES): shown neutral, never as an alert. */
export const INFORMATIONAL_HEALTH_CODES: readonly string[] = ['no_active_login'];

export function HealthChips({ flags }: { flags: HealthFlag[] | null }) {
  if (flags === null) return null;
  if (flags.length === 0) return <span className="fos-chip healthy"><Icon name="check" size={12} />Healthy</span>;
  return <>{flags.map((f) => (INFORMATIONAL_HEALTH_CODES.includes(f.code)
    ? <span key={f.code} className="fos-chip info" title={f.detail}><Icon name="user" size={12} />{f.label}</span>
    : <span key={f.code} className="fos-chip attention" title={f.detail}><Icon name="alert" size={12} />{f.label}</span>))}</>;
}

export function EmptyState({ icon = 'heart', title, children }: { icon?: string; title: string; children?: ReactNode }) {
  return (
    <div className="fos-empty">
      <span className="fos-empty-icon"><Icon name={icon} size={22} /></span>
      <strong>{title}</strong>
      {children ? <p>{children}</p> : null}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return message ? <div className="fos-error" role="alert">{message}</div> : null;
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function Section({ title, icon, action, children, className = '' }: { title: string; icon?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`fos-card ${className}`}>
      <header className="fos-card-head">
        <h3>{icon ? <Icon name={icon} size={16} /> : null}{title}</h3>
        {action ? <div className="fos-card-action">{action}</div> : null}
      </header>
      {children}
    </section>
  );
}
